import { useMemo } from 'react';

import { Button, Divider, Stack, styled, Typography } from '@mui/material';

import { AppModal, BaseModalProps } from '@/components';
import { useCFSContext } from '@/hooks';
import { AssignedUnitShiftStatus } from '@/models';
import { useAgencyStore } from '@/store';

const CFSAgencyUnassignModalRoot = styled(AppModal)(({ theme }) => ({
  '& .modal-content': {
    position: 'absolute',
    minWidth: 500,
    padding: 20,
    borderRadius: 8,
    [theme.breakpoints.up('lg')]: {
      maxWidth: 600,
    },
    [theme.breakpoints.up('xl')]: {
      maxWidth: 600,
    },

    '& .btn-close': {
      position: 'absolute',
      right: 8,
      top: 8,
      color: theme.palette.error.main,
    },
    '& .separator': {
      marginTop: 20,
      marginBottom: 20,
    },
  },
}));

interface CFSAgencyUnassignModalProps extends Omit<BaseModalProps, 'onClose'> {
  onSubmit: () => void;
  open: boolean;
  onClose: () => void;
  agencyId: string;
}

export const CFSAgencyUnassignModal = ({
  agencyId,
  onClose,
  open,
  onSubmit,
}: CFSAgencyUnassignModalProps) => {
  const { agencies } = useAgencyStore();
  const { cfs } = useCFSContext();

  const unassigningAgency = agencies.find((a) => a._id === agencyId);

  const units = useMemo(() => {
    if (!cfs?.assignedUnitShifts) return [];

    return (
      cfs?.assignedUnitShifts
        ?.filter(
          (au) =>
            au?.unitShift?.unit?.agency === agencyId &&
            au.status !== AssignedUnitShiftStatus.CANCELED &&
            au.status !== AssignedUnitShiftStatus.COMPLETED,
        )
        .map((au) => au?.unitShift?.unit?.name) || []
    );
  }, [cfs, agencyId]);

  return (
    <CFSAgencyUnassignModalRoot
      onClose={onClose}
      open={open}
      title="Remove Agency with Assigned Units"
      hideCloseButton
    >
      <Stack rowGap={2} divider={<Divider />}>
        <Typography>
          Unassigning {unassigningAgency?.name} will automatically unassign
          these units:
          <Stack>
            {units?.map((u) => (
              <Stack key={u} sx={{ px: 2 }}>
                • {u}
              </Stack>
            ))}
          </Stack>
        </Typography>

        <Stack flexDirection="row" justifyContent="space-between">
          <Button variant="outlined" color="inherit" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={onSubmit}>
            Unassign
          </Button>
        </Stack>
      </Stack>
    </CFSAgencyUnassignModalRoot>
  );
};
